import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  AgGridEvent,
  CellClickedEvent,
  Column,
  ColumnApi,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  RowSelectedEvent,
} from '@ag-grid-community/core';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { forkJoin, from, Observable, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SpinnerModalComponent } from 'Src/ng2/shared/components/spinner/spinner-modal.component';
import { SpinnerService } from 'Src/ng2/shared/components/spinner/spinner-modal.service';
import { NgRouterNavigationService } from '../../../school/lists/services/list-navigation/ng-router-list-navigation.service';
import { ObjectCache } from '../../../shared/services/object-cache/object-cache.service';
import { getBatchActionsState, ToggleBatchActions, UpdateSelectedStudentIds } from '../../../store';
import { AttendanceGridService } from './services/attendance-grid.service';

@Component({
  selector: 'shelter-att-grid',
  template: require('./shelter-att-grid.component.html'),
  styles: [require('./shelter-att-grid.component.scss')],
})
export class ShelterAttGridComponent implements OnInit, OnDestroy {
  public modules: any[] = [
    ClientSideRowModelModule,
    ClipboardModule,
    MenuModule,
    RangeSelectionModule,
    SetFilterModule,
    StatusBarModule,
  ];

  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  private shelterId: string;
  public batchActionMode: boolean = false;
  public columnDefs: any[];
  public disableToolButtons: boolean = true;
  public gridOptions: GridOptions;
  public numRowsSelected: number = 0;
  public rowData: any[];
  public spinner: MatDialogRef<SpinnerModalComponent>;

  constructor(
    private attendanceGridService: AttendanceGridService,
    private ngRouterNavigationService: NgRouterNavigationService,
    private objectCache: ObjectCache,
    private route: ActivatedRoute,
    private store: Store<any>,
    private spinnerService: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.spinner = this.spinnerService.openSpinner({
      message: 'Loading...',
    });
    this.shelterId = this.route.snapshot.params.shelterId;

    this.attendanceGridService
      .getAllShelterData$(this.shelterId)
      .pipe(
        tap(data => {
          const { columnDefs, rowData } = data;
          this.setColumnDefs(columnDefs);
          this.setRowData(rowData);
          if (rowData.length === 0) {
            // if rowData is zero then onFirstDataRendered will never be called and the
            // spinner will not close
            this.closeSpinner();
          }
        }),
      )
      .subscribe();

    this.gridOptions = <GridOptions>{
      headerHeight: 48,
      rowHeight: 32, // make sure this matches the CSS
      animateRows: true,
      tooltipShowDelay: 500, // default is 2000 ms
      valueCache: true, // cache value getters
      enableRangeSelection: true,
      suppressCsvExport: true,
      statusBar: {
        statusPanels: [
          {
            statusPanel: 'agTotalAndFilteredRowCountComponent',
            align: 'left',
          },
          {
            statusPanel: 'agTotalRowCountComponent',
            align: 'center',
          },
          { statusPanel: 'agFilteredRowCountComponent' },
          { statusPanel: 'agSelectedRowCountComponent' },
          { statusPanel: 'agAggregationComponent' },
        ],
      },
    };
  }

  private setColumnDefs(columnDefs: any[]): void {
    this.columnDefs = columnDefs;
  }

  private setRowData(rowData: any[]): void {
    this.rowData = rowData;
  }

  public onGridReady(params: AgGridEvent): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  public onFirstDataRendered(params: FilterChangedEvent): void {
    const { api, columnApi } = params;
    const sortFilterModel$ = this.getSortFilterModelFromRoute();
    sortFilterModel$
      .pipe(
        tap(({ sort, filter }) => {
          if (sort) {
            api.setSortModel(sort);
            api.onSortChanged();
          }
          if (filter) {
            api.setFilterModel(filter);
            api.onFilterChanged();
          }
        }),
      )
      .subscribe();
    this.loadBatchActionState$()
      .pipe(
        tap(batchActionState => {
          this.selectRowsById(batchActionState);
        }),
      )
      .subscribe();
    this.autoSizeColumns(columnApi);
    this.closeSpinner();
  }

  private closeSpinner() {
    this.spinner.close();
  }

  public onFilterChanged(params: FirstDataRenderedEvent): void {
    const model = params.api.getFilterModel();
    let filterHash: string | null;
    if (isEmpty(model)) {
      filterHash = null;
    } else {
      filterHash = this.objectCache.cacheObject(model);
    }
    const extras = {
      relativeTo: this.route,
      queryParams: {
        filter: filterHash,
      },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    this.ngRouterNavigationService.updateUrl([], extras);
  }

  public onSortChanged(params: AgGridEvent): void {
    const model = params.api.getSortModel();
    let sortHash: string | null;
    if (model.length) {
      sortHash = this.objectCache.cacheObject(model);
    } else {
      sortHash = null;
    }
    const extras = {
      relativeTo: this.route,
      queryParams: {
        sort: sortHash,
      },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    this.ngRouterNavigationService.updateUrl([], extras);
  }

  public onCellClicked(params: CellClickedEvent): void {
    const {
      colDef: { lockPinned },
    } = params;
    if (lockPinned && !this.batchActionMode) {
      const {
        data: { CARES_ID },
      } = params;
      const caresId = [CARES_ID];
      const payload = {
        caresIds: caresId,
        updateRelPath: `shelter/${this.shelterId}/profile`,
        queryParamsHandling: 'merge',
        route: null,
      };
      this.ngRouterNavigationService.goToProfile(payload);
    }
  }

  public onRowSelected(params: RowSelectedEvent): void {
    const selectedRows = params.api.getSelectedRows();
    this.numRowsSelected = selectedRows.length;
    this.disableToolButtons = selectedRows.length ? false : true;
  }

  public onCancel(): void {
    this.gridApi.deselectAll();
    this.nullCheckboxSelectionOptions();
    this.clearBatchActionState();
    this.gridOptions.rowSelection = 'single';
    this.batchActionMode = false;
  }

  public onBatchActions(): void {
    const colState = this.gridColumnApi.getColumnState();
    const fistColumnId = colState[0].colId;
    this.columnDefs = this.columnDefs.map(c => {
      if (c.field === fistColumnId) {
        c.checkboxSelection = true;
        c.headerCheckboxSelection = true;
        c.headerCheckboxSelectionFilteredOnly = true;
      }
      return c;
    });
    this.gridOptions.rowSelection = 'multiple';
    this.batchActionMode = true;
  }

  private autoSizeColumns(gridColumnApi: ColumnApi): void {
    const columnIds = gridColumnApi
      .getAllColumns()
      .filter((column: Column) => {
        const {
          colDef: { width },
        } = column;
        return !width;
      })
      .reduce((acc, column: Column) => {
        acc.push(column.colId);
        return acc;
      }, []);
    gridColumnApi.autoSizeColumns(columnIds);
  }

  private getSortFilterModelFromRoute(): Observable<any> {
    const { sort, filter } = this.route.snapshot.queryParams;
    const sort$ = sort ? from(this.objectCache.getObject(sort)) : of(null);
    const filter$ = filter ? from(this.objectCache.getObject(filter)) : of(null);
    return forkJoin({ sort: sort$, filter: filter$ });
  }

  public goToList(): void {
    const url = `shelter/${this.shelterId}/lists/attendance-list`;
    this.clearBatchActionState();
    this.ngRouterNavigationService.updateUrl([url]);
  }

  public onViewProfiles(): void {
    const selectedRows = this.gridApi.getSelectedRows();
    const caresIds = selectedRows.map(row => row.CARES_ID);
    const payload = {
      caresIds,
      updateRelPath: `shelter/${this.shelterId}/profile`,
      queryParamsHandling: 'merge',
      route: null,
    };
    this.saveBatchActionState(caresIds);
    this.ngRouterNavigationService.goToProfile(payload);
  }

  private saveBatchActionState(caresIds: string[]): void {
    this.store.dispatch(new ToggleBatchActions(true));
    this.store.dispatch(new UpdateSelectedStudentIds(caresIds as any));
  }

  private clearBatchActionState(): void {
    this.store.dispatch(new ToggleBatchActions(false));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }

  private loadBatchActionState$(): Observable<any> {
    return this.store.select(getBatchActionsState).pipe(take(1));
  }

  private selectRowsById(batchActionState: { enabled: boolean; selectedStudentIds: string[] }): void {
    const { enabled, selectedStudentIds } = batchActionState;
    if (enabled) {
      this.onBatchActions();
      const idHash = selectedStudentIds.reduce((acc: any, id: string) => {
        acc[id] = true;
        return acc;
      }, {});
      this.gridApi.forEachNode(node => {
        node.setSelected(idHash[node.data.CARES_ID]);
      });
    }
  }

  private nullCheckboxSelectionOptions() {
    this.columnDefs = this.columnDefs.map(c => {
      c.checkboxSelection = null;
      c.headerCheckboxSelection = null;
      c.headerCheckboxSelectionFilteredOnly = null;
      return c;
    });
  }

  public ngOnDestroy() {
    this.nullCheckboxSelectionOptions();
  }
}
